import { axios_adapter } from '../network/axios_adapter';
import ENDPOINTS from '../network/endpoints';
import { getUserProfile } from '../storage/userProfileStorage';

const HOW_IT_WORKS_STEPS = [
  'Invite your friends and family to register on Rapchik.',
  'When your friends and family register on the app, both of you will get 1000 RapCoins.',
  'RapCoins can be used to let you get entry in a game and in play next contest .',
];

function pickString(item, keys, fallback = '') {
  for (const key of keys) {
    const value = item?.[key];
    if (value != null && value !== '') return String(value).trim();
  }
  return fallback;
}

function pickNumber(item, keys) {
  for (const key of keys) {
    const value = item?.[key];
    if (value != null && value !== '') {
      const parsed = Number(value);
      if (!Number.isNaN(parsed)) return parsed;
    }
  }
  return null;
}

function formatReferralCode(code = '') {
  const trimmed = String(code ?? '').trim();
  if (!trimmed) return '';
  return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
}

function formatReferredCount(value) {
  if (value == null || value === '') return '00';
  const count = Number(value);
  if (!Number.isFinite(count)) return String(value);
  return String(count).padStart(2, '0');
}

function unwrapStepsList(rawSteps) {
  if (Array.isArray(rawSteps)) return rawSteps;
  if (rawSteps && typeof rawSteps === 'object') {
    return rawSteps.steps ?? rawSteps.items ?? rawSteps.list ?? null;
  }
  return null;
}

export function resolveReferralStepText(step) {
  if (typeof step === 'string') return step.trim();
  if (!step || typeof step !== 'object') return '';

  if (typeof step.text === 'string') return step.text.trim();
  if (typeof step.description === 'string') return step.description.trim();
  if (typeof step.step === 'string') return step.step.trim();
  if (typeof step.content === 'string') return step.content.trim();
  if (typeof step.heading === 'string') return step.heading.trim();

  if (typeof step.title === 'string') return step.title.trim();
  if (step.title && typeof step.title === 'object') {
    return resolveReferralStepText(step.title);
  }

  return pickString(step, ['text', 'description', 'title', 'step', 'content', 'heading', 'label']);
}

function normalizeHowItWorksSteps(rawSteps) {
  const list = unwrapStepsList(rawSteps);
  if (!Array.isArray(list) || !list.length) return [...HOW_IT_WORKS_STEPS];

  const steps = list.map((step) => resolveReferralStepText(step)).filter(Boolean);

  return steps.length ? steps : [...HOW_IT_WORKS_STEPS];
}

const RAPCOIN_STAT_KEYS = [
  'totalReferralEarnings',
  'total_referral_earnings',
  'totalRapCoinsEarned',
  'total_rap_coins_earned',
  'rapCoinsEarned',
  'rap_coins_earned',
  'coinsEarned',
  'coins_earned',
  'totalEarned',
  'total_earned',
  'earnedRapCoins',
  'earned_rap_coins',
  'totalCoinsEarned',
  'total_coins_earned',
  'earnedCoins',
  'earned_coins',
  'referralCoins',
  'referral_coins',
  'totalReferralCoins',
  'total_referral_coins',
  'rewardCoins',
  'reward_coins',
];

const REFERRED_STAT_KEYS = [
  'friendsReferred',
  'friends_referred',
  'totalReferred',
  'total_referred',
  'referredCount',
  'referred_count',
  'referralCount',
  'referral_count',
  'totalReferrals',
  'total_referrals',
  'referredUsersCount',
  'referred_users_count',
  'totalReferredUsers',
  'total_referred_users',
  'successfulReferrals',
  'successful_referrals',
];

const REFERRAL_LIST_KEYS = [
  'myReferrals',
  'referrals',
  'referredUsers',
  'referredList',
  'users',
  'items',
  'list',
  'records',
];

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function extractReferralList(data) {
  if (Array.isArray(data)) return data;
  if (!isPlainObject(data)) return [];

  const sources = [
    data,
    data.referral,
    data.myReferral,
    data.stats,
    data.summary,
    data.referralStats,
  ].filter(isPlainObject);

  for (const source of sources) {
    for (const key of REFERRAL_LIST_KEYS) {
      if (Array.isArray(source[key])) return source[key];
    }
  }

  return [];
}

function sumReferralListCoins(list) {
  return list.reduce((sum, item) => {
    if (!isPlainObject(item)) return sum;
    return sum + (pickNumber(item, RAPCOIN_STAT_KEYS) ?? pickNumber(item, ['coins', 'amount', 'reward']) ?? 0);
  }, 0);
}

function pickFromSources(sources, keys) {
  for (const source of sources) {
    const value = pickNumber(source, keys);
    if (value != null) return value;
  }
  return null;
}

function mapReferralData(item = {}, list = [], pagination = {}) {
  const profile = getUserProfile() || {};
  const referralCode = formatReferralCode(
    pickString(item, ['referralCode', 'referral_code', 'code', 'inviteCode', 'invite_code', 'myReferralCode', 'my_referral_code']) ||
      pickString(profile, ['referralCode', 'referral_code', 'code', 'inviteCode']),
  );
  const plainCode = referralCode.replace(/^#/, '');
  const shareUrl =
    pickString(item, ['shareUrl', 'share_url', 'referralLink', 'referral_link', 'inviteLink', 'invite_link', 'shareLink', 'share_link'], '') ||
    (plainCode ? `${window.location.origin}/?ref=${encodeURIComponent(plainCode)}` : '');

  return {
    totalRapCoinsEarned:
      pickFromSources([item], RAPCOIN_STAT_KEYS) ?? (list.length ? sumReferralListCoins(list) : 0),
    totalReferred:
      pickFromSources([item, pagination], REFERRED_STAT_KEYS) ??
      pickNumber(pagination, ['total', 'count']) ??
      list.length,
    referralCode,
    shareUrl,
    shareMessage:
      pickString(
        item,
        ['shareMessage', 'share_message', 'message', 'shareText', 'share_text'],
        referralCode
          ? `Join me on Rapchik! Use my referral code ${referralCode} and earn rewards.`
          : 'Join me on Rapchik!',
      ),
    howItWorksSteps: normalizeHowItWorksSteps(
      item?.howItWorksSteps ??
        item?.howItWorks ??
        item?.howDoesItWork ??
        item?.how_it_works ??
        item?.steps,
    ),
  };
}

function extractReferralPayload(body) {
  const root = body?.data ?? body;
  const list = extractReferralList(root);
  const record = Array.isArray(root) ? {} : isPlainObject(root) ? { ...root } : {};
  const nestedRecord = [record.referral, record.myReferral, record.stats, record.summary, record.referralStats].find(
    (value) => isPlainObject(value),
  );

  if (nestedRecord) {
    Object.assign(record, nestedRecord);
  }

  const pagination = isPlainObject(record.pagination) ? record.pagination : isPlainObject(body?.pagination) ? body.pagination : {};

  return { record, list, pagination };
}

export async function fetchReferralData() {
  const response = await axios_adapter.get(ENDPOINTS.USER_REFERRALS);
  const body = response.data;

  if (body?.responseCode && body.responseCode !== 200) {
    throw new Error(body?.data?.message || body?.message || 'Unable to load referrals.');
  }

  const { record, list, pagination } = extractReferralPayload(body);
  return mapReferralData(record, list, pagination);
}

export function getFormattedReferredCount(totalReferred) {
  return formatReferredCount(totalReferred);
}

export async function copyReferralCode(referralCode) {
  const plainCode = String(referralCode || '').replace(/^#/, '');
  if (!plainCode) throw new Error('Referral code is not available.');
  await navigator.clipboard.writeText(plainCode);
  return plainCode;
}

export async function shareReferralLink(data) {
  const shareData = {
    title: 'Join Rapchik',
    text: data?.shareMessage || 'Join me on Rapchik!',
    url: data?.shareUrl,
  };

  if (navigator.share) {
    await navigator.share(shareData);
    return true;
  }

  if (data?.shareUrl) {
    await navigator.clipboard.writeText(data.shareUrl);
    return false;
  }

  throw new Error('Unable to share referral link.');
}
